import { assertTokenValidForMutation, handleResponse } from "@/services/service-helpers.ts"

const NBH_IMPORT_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/import`

// Type definitions based on backend Response DTOs

export interface ImportSessionResponse {
    id: string
    status: ImportStatus
    phase: ImportPhase
    startedAt: string  // ISO 8601 timestamp
}

export interface ImportStatusResponse {
    id: string
    status: ImportStatus
    phase: ImportPhase
    progress: number  // 0-100
    startedAt: string  // ISO 8601 timestamp
    completedAt: string | null  // ISO 8601 timestamp
    errors: string | null
    warnings: string | null
    socialAccountDetectedCredits: number | null
    socialAccountDetectedDebits: number | null
    socialAccountDetectedNet: number | null
    socialAccountConfirmationStatus: SocialAccountImportConfirmationStatus | null
}

export type SocialAccountImportConfirmationStatus = 'PENDING' | 'CONFIRMED' | 'NOT_APPLICABLE'

export interface SocialAccountConfirmationResponse {
    balance: number
}

export interface ImportSessionSummary {
    id: string
    status: ImportStatus
    startedAt: string  // ISO 8601 timestamp
    completedAt: string | null  // ISO 8601 timestamp
    importedMembersCount: number | null
    importedOrdersCount: number | null
    dataStartDate: string | null  // ISO 8601 date (YYYY-MM-DD)
    dataEndDate: string | null  // ISO 8601 date (YYYY-MM-DD)
    durationSeconds: number | null
}

export type ImportStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'
export type ImportPhase = 'PREPARATION' | 'REPLICATION' | 'MAPPING' | 'FINALIZATION'

/**
 * Uploads an MDB file and starts the import process.
 * The import runs synchronously on the backend.
 *
 * @param file - The MDB file to upload
 * @param token - JWT authentication token
 * @param openingBalanceAmount - Optional opening balance amount
 * @param openingBalanceDate - Optional opening balance date (YYYY-MM-DD)
 * @returns Promise with import session response
 * @throws Error if upload fails
 */
export async function uploadMdbFile(
    file: File,
    token: string,
    openingBalanceAmount?: number,
    openingBalanceDate?: string
): Promise<ImportSessionResponse> {
    assertTokenValidForMutation()
    const formData = new FormData()
    formData.append('file', file)

    if (openingBalanceAmount != null && openingBalanceDate != null) {
        formData.append('openingBalanceAmount', openingBalanceAmount.toString())
        formData.append('openingBalanceDate', openingBalanceDate)
    }

    const response = await fetch(`${NBH_IMPORT_BASE_URL}/mdb`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`
            // NO Content-Type header for FormData - browser sets it automatically with boundary
        }),
        body: formData
    })

    return handleResponse<ImportSessionResponse>(response) as Promise<ImportSessionResponse>
}

/**
 * Gets the current or most recent import status for the organization.
 *
 * @param token - JWT authentication token
 * @returns Promise with import status response, or null if no imports exist
 * @throws Error if request fails (except 404)
 */
export async function getImportStatus(token: string): Promise<ImportStatusResponse | null> {
    const response = await fetch(`${NBH_IMPORT_BASE_URL}/status`, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    })

    // 404 means no imports exist yet - return null instead of throwing
    if (response.status === 404) {
        return null
    }

    return handleResponse<ImportStatusResponse>(response) as Promise<ImportStatusResponse>
}

/**
 * Gets the import history for the organization.
 * Returns all import sessions ordered by start time (newest first).
 *
 * @param token - JWT authentication token
 * @returns Promise with array of import session summaries (empty if none exist)
 * @throws Error if request fails
 */
export async function getImportHistory(token: string): Promise<ImportSessionSummary[]> {
    const response = await fetch(`${NBH_IMPORT_BASE_URL}/sessions`, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    })

    return handleResponse<ImportSessionSummary[]>(response) as Promise<ImportSessionSummary[]>
}

// Books the confirmed or overridden Sozialkonto opening balance for a completed import.
export async function confirmSocialAccountBalance(
    sessionId: string,
    amount: number,
    token: string
): Promise<SocialAccountConfirmationResponse> {
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_IMPORT_BASE_URL}/${sessionId}/social-account/confirm`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify({ amount })
    })

    return handleResponse<SocialAccountConfirmationResponse>(response) as Promise<SocialAccountConfirmationResponse>
}
